import React, { useContext, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../config/api";
import Sidebar from "../../components/Sidebar";
import {
  Eye,
  Menu,
  Search,
  PackageSearch,
  X,
} from "lucide-react";
const formatDate = (dateValue) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileDetailOrder, setMobileDetailOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("all");
  // ================= FETCH ORDERS =================
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/orders`);
        if (!res.ok) throw new Error("Server error");
        const data = await res.json();
        const formatted = (data || []).map((order) => {
          let product = null;
          try {
            product = order.order_details
              ? JSON.parse(order.order_details)
              : null;
          } catch {
            product = null;
          }
          return {
            id: order.id,
            name: order.name,
            surname: order.surname,
            email: order.email,
            mobile: order.mobile,
            address: order.address,
            status: order.status || "pending",
            createdAt: order.created_at,
            product,
          };
        });
        setOrders(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);
  // ================= STATUS UPDATE =================
  const handleStatusChange = async (id, status) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/orders/${id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || "Failed");
        return;
      }
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
    } catch (err) {
      alert("Server error");
    }
  };
  // ================= DATE FILTER =================
  const isInDateRange = (date) => {
    const d = new Date(date);
    const now = new Date();
    if (dateFilter === "today") {
      return d.toDateString() === now.toDateString();
    }
    if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      return d >= weekAgo;
    }
    if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(now.getMonth() - 1);
      return d >= monthAgo;
    }
    return true;
  };
  // ================= FILTERED DATA =================
  const filteredOrders = orders.filter((o) => {
    const matchSearch =
      o.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.surname?.toLowerCase().includes(search.toLowerCase()) ||
      o.mobile?.includes(search);
    const matchStatus =
      statusFilter === "All" ? true : o.status === statusFilter;
    const matchDate = isInDateRange(formatDate(o.createdAt));
    return matchSearch && matchStatus && matchDate;
  });
  // ================= EXPORT EXCEL =================
  const exportExcel = () => {
    const exportData = filteredOrders.map((o) => ({
      ID: o.id,
      Emri: o.name,
      Mbiemri: o.surname,
      Telefoni: o.mobile,
      Statusi: o.status,
      Data: formatDate(o.createdAt),
      Produkti: o.product?.name || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "orders.xlsx");
  };
  return (
    <div className="orders-design min-h-screen overflow-x-hidden bg-gray-100 text-gray-800">
      <style>{ordersDesign}</style>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      {/* HAMBURGER - VETËM MOBILE */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="
        fixed
        left-4
        top-4
        z-30
        flex
        h-11
        w-11
        items-center
        justify-center
        rounded-xl
        bg-blue-700
        text-white
        shadow-lg
        transition-all
        duration-200
        ease-out
        hover:bg-blue-800
        active:scale-95
        lg:hidden
      "
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>
      <main
        className="
    min-h-screen min-w-0
    overflow-x-hidden
    px-2 pb-8 pt-20
    sm:px-4
    lg:ml-64
    lg:w-[calc(100%-16rem)]
    lg:overflow-visible
    lg:p-8
  "
      >
        {/* =====================================================
      HEADER
  ===================================================== */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Menaxhimi i porosive
            </span>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              Porositë
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Shiko, filtro dhe menaxho porositë e klientëve.
            </p>
          </div>
          <button
            type="button"
            onClick={exportExcel}
            className="
        w-full rounded-xl
        bg-green-600
        px-5 py-3
        font-semibold text-white
        shadow-md transition
        hover:bg-green-700
        active:scale-[0.98]
        sm:w-auto
      "
          >
            Export Excel
          </button>
        </div>
        {/* =====================================================
      ERROR
  ===================================================== */}
        {error && (
          <div
            className="
        mb-5 rounded-xl
        border border-red-200
        bg-red-50
        px-4 py-3
        text-sm font-medium
        text-red-700
      "
          >
            {error}
          </div>
        )}
        {/* =====================================================
      FILTERS
  ===================================================== */}
        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {/* SEARCH */}
            <div className="relative sm:col-span-2">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Kërko emër, mbiemër, telefon, produkt..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="
            w-full rounded-xl
            border border-gray-300
            py-2.5 pl-10 pr-3
            outline-none
            transition
            focus:border-blue-500
            focus:ring-4
            focus:ring-blue-100
          "
              />
            </div>
            {/* STATUS */}
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value)
              }
              className="
          w-full rounded-xl
          border border-gray-300
          bg-white px-3 py-2.5
          outline-none
          transition
          focus:border-blue-500
          focus:ring-4
          focus:ring-blue-100
        "
            >
              <option value="All">
                Të gjitha statuset
              </option>
              <option value="pending">
                Në pritje
              </option>
              <option value="confirmed">
                Konfirmuar
              </option>
              <option value="shipped">
                Dërguar
              </option>
              <option value="cancelled">
                Anuluar
              </option>
            </select>
            {/* DATE */}
            <select
              value={dateFilter}
              onChange={(e) =>
                setDateFilter(e.target.value)
              }
              className="
          w-full rounded-xl
          border border-gray-300
          bg-white px-3 py-2.5
          outline-none
          transition
          focus:border-blue-500
          focus:ring-4
          focus:ring-blue-100
        "
            >
              <option value="all">
                Çdo periudhë
              </option>
              <option value="today">
                Sot
              </option>
              <option value="week">
                7 ditët e fundit
              </option>
              <option value="month">
                Muaji i fundit
              </option>
            </select>
          </div>
          {/* RESET FILTERS */}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("All");
                setDateFilter("all");
              }}
              className="
          w-full rounded-xl
          bg-gray-100
          px-4 py-2.5
          font-semibold text-gray-700
          transition
          hover:bg-gray-200
          sm:w-auto
        "
            >
              Pastro filtrat
            </button>
          </div>
        </section>
        {/* =====================================================
      RESULT INFO
  ===================================================== */}
        {!loading && (
          <div
            className="
        mb-3
        flex items-center justify-between
        rounded-xl
        border border-blue-100
        bg-blue-50
        px-4 py-3
        text-sm text-blue-700
      "
          >
            <span>
              <strong>
                {filteredOrders.length}
              </strong>{" "}
              porosi të gjetura
            </span>
            <span className="hidden sm:inline">
              Regjistri i porosive
            </span>
          </div>
        )}
        {/* =====================================================
      TABLE
  ===================================================== */}
        <section className="hidden w-full max-w-full overflow-hidden rounded-2xl bg-white shadow-sm md:block">
          {loading ? (
            <div className="px-4 py-16 text-center text-gray-500">
              Duke ngarkuar porositë...
            </div>
          ) : filteredOrders.length > 0 ? (
            <div
              className="
          overflow-x-auto
          overscroll-x-contain
          [-webkit-overflow-scrolling:touch]
        "
            >
              <table className="min-w-[1100px] w-full text-sm">
                {/* TABLE HEADER */}
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="whitespace-nowrap p-3 text-left">
                      ID
                    </th>
                    <th className="whitespace-nowrap p-3 text-left">
                      Emri
                    </th>
                    <th className="whitespace-nowrap p-3 text-left">
                      Mbiemri
                    </th>
                    <th className="whitespace-nowrap p-3 text-left">
                      Telefoni
                    </th>
                    <th className="whitespace-nowrap p-3 text-left">
                      Produkti
                    </th>
                    <th className="whitespace-nowrap p-3 text-left">
                      Statusi
                    </th>
                    <th className="whitespace-nowrap p-3 text-left">
                      Data
                    </th>
                  </tr>
                </thead>
                {/* TABLE BODY */}
                <tbody>
                  {filteredOrders.map((o) => (
                    <tr
                      key={o.id}
                      className="
                  border-b border-gray-100
                  transition-colors
                  hover:bg-blue-50/40
                "
                    >
                      {/* ID */}
                      <td className="whitespace-nowrap p-3 font-medium text-gray-600">
                        #{o.id}
                      </td>
                      {/* NAME */}
                      <td className="whitespace-nowrap p-3 font-bold text-gray-900">
                        {o.name || "—"}
                      </td>
                      {/* SURNAME */}
                      <td className="whitespace-nowrap p-3">
                        {o.surname || "—"}
                      </td>
                      {/* MOBILE */}
                      <td className="whitespace-nowrap p-3">
                        {o.mobile || "—"}
                      </td>
                      {/* PRODUCT */}
                      <td className="max-w-[260px] p-3">
                        <span className="block truncate font-semibold text-gray-800">
                          {o.product?.name ||
                            "Pa produkt"}
                        </span>
                      </td>
                      {/* STATUS */}
                      <td className="whitespace-nowrap p-3">
                        <select
                          value={o.status}
                          onChange={(e) =>
                            handleStatusChange(
                              o.id,
                              e.target.value
                            )
                          }
                          className={`
                      rounded-lg
                      border px-3 py-1.5
                      text-sm font-semibold
                      outline-none
                      transition
                      focus:ring-2
                      ${o.status === "pending"
                              ? "border-yellow-200 bg-yellow-50 text-yellow-700 focus:ring-yellow-100"
                              : o.status === "confirmed"
                                ? "border-blue-200 bg-blue-50 text-blue-700 focus:ring-blue-100"
                                : o.status === "shipped"
                                  ? "border-green-200 bg-green-50 text-green-700 focus:ring-green-100"
                                  : o.status === "cancelled"
                                    ? "border-red-200 bg-red-50 text-red-700 focus:ring-red-100"
                                    : "border-gray-300 bg-white text-gray-700 focus:ring-gray-100"
                            }
                    `}
                        >
                          <option value="pending">
                            Në pritje
                          </option>
                          <option value="confirmed">
                            Konfirmuar
                          </option>
                          <option value="shipped">
                            Dërguar
                          </option>
                          <option value="cancelled">
                            Anuluar
                          </option>
                        </select>
                      </td>
                      {/* DATE */}
                      <td className="whitespace-nowrap p-3 text-gray-600">
                        {formatDate(o.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* EMPTY STATE */
            <div className="px-4 py-16 text-center text-gray-500">
              <PackageSearch
                size={42}
                className="mx-auto mb-3 text-gray-300"
              />
              <p className="font-semibold">
                Nuk u gjet asnjë porosi.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Ndrysho filtrat për të kërkuar porosi të tjera.
              </p>
            </div>
          )}
        </section>
        {/* =====================================================
      MOBILE ORDERS
      Desktop-i mbetet i pandryshuar.
  ===================================================== */}
        <section className="space-y-3 md:hidden">
          {loading ? (
            <div className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-gray-500 shadow-sm">
              Duke ngarkuar porositë...
            </div>
          ) : filteredOrders.length > 0 ? (
            filteredOrders.map((o) => {
              const statusClass =
                o.status === "pending"
                  ? "bg-yellow-100 text-yellow-700"
                  : o.status === "confirmed"
                    ? "bg-blue-100 text-blue-700"
                    : o.status === "shipped"
                      ? "bg-green-100 text-green-700"
                      : o.status === "cancelled"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-700";
              const statusLabel =
                o.status === "pending"
                  ? "Në pritje"
                  : o.status === "confirmed"
                    ? "Konfirmuar"
                    : o.status === "shipped"
                      ? "Dërguar"
                      : o.status === "cancelled"
                        ? "Anuluar"
                        : o.status;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setMobileDetailOrder(o)}
                  className="
              block w-full
              rounded-2xl
              border border-gray-200
              bg-white
              p-4
              text-left
              shadow-sm
              transition
              active:scale-[0.99]
            "
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-blue-600">
                        Porosia #{o.id}
                      </span>
                      <h3 className="mt-1 truncate text-lg font-extrabold text-gray-900">
                        {o.name || "—"} {o.surname || ""}
                      </h3>
                      <p className="mt-1 text-sm font-medium text-gray-500">
                        {o.mobile || "Pa telefon"}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${statusClass}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <div className="mt-4 rounded-2xl bg-gray-50 p-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Produkti
                    </span>
                    <p className="mt-1 line-clamp-2 text-sm font-bold text-gray-800">
                      {o.product?.name || "Pa produkt"}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
                    <span className="text-xs text-gray-400">
                      {formatDate(o.createdAt)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
                      Shiko detajet
                      <Eye size={16} />
                    </span>
                  </div>
                </button>
              );
            })
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
              <PackageSearch
                size={42}
                className="mx-auto mb-3 text-gray-300"
              />
              <p className="font-semibold text-gray-700">
                Nuk u gjet asnjë porosi.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Ndrysho filtrat për të kërkuar porosi të tjera.
              </p>
            </div>
          )}
        </section>
        {/* MOBILE ORDER DETAIL MODAL */}
        {mobileDetailOrder && (
          <div
            className="
        fixed inset-0 z-[9998]
        flex items-end justify-center
        bg-black/55
        backdrop-blur-sm
        sm:items-center
        sm:p-4
        md:hidden
      "
            onClick={() => setMobileDetailOrder(null)}
          >
            <div
              className="
          flex
          max-h-[94dvh]
          w-full
          flex-col
          overflow-hidden
          rounded-t-3xl
          bg-white
          shadow-2xl
          sm:max-w-lg
          sm:rounded-3xl
        "
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 bg-gradient-to-r from-blue-700 to-indigo-700 px-5 py-5 text-white">
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">
                    Detajet e porosisë
                  </span>
                  <h2 className="mt-1 truncate text-xl font-extrabold">
                    {mobileDetailOrder.name || "—"}{" "}
                    {mobileDetailOrder.surname || ""}
                  </h2>
                  <p className="mt-1 text-xs text-blue-100">
                    Porosia #{mobileDetailOrder.id}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileDetailOrder(null)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10"
                  aria-label="Mbyll"
                >
                  <X size={19} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Telefoni
                    </span>
                    <strong className="mt-1 block break-words text-sm text-gray-800">
                      {mobileDetailOrder.mobile || "—"}
                    </strong>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Email
                    </span>
                    <strong className="mt-1 block break-words text-sm text-gray-800">
                      {mobileDetailOrder.email || "—"}
                    </strong>
                  </div>
                  <div className="col-span-2 rounded-2xl bg-gray-50 p-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Adresa
                    </span>
                    <strong className="mt-1 block break-words text-sm text-gray-800">
                      {mobileDetailOrder.address || "—"}
                    </strong>
                  </div>
                  <div className="col-span-2 rounded-2xl bg-gray-50 p-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                      Data
                    </span>
                    <strong className="mt-1 block text-sm text-gray-800">
                      {mobileDetailOrder.createdAt
                        ? new Date(
                          mobileDetailOrder.createdAt
                        ).toLocaleString("sq-AL")
                        : "—"}
                    </strong>
                  </div>
                </div>
                <section className="mt-4 rounded-2xl border border-gray-200 p-4">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Produkti
                  </span>
                  <h3 className="mt-2 text-base font-extrabold text-gray-900">
                    {mobileDetailOrder.product?.name || "Pa produkt"}
                  </h3>
                  {mobileDetailOrder.product &&
                    Object.entries(mobileDetailOrder.product)
                      .filter(
                        ([key, value]) =>
                          key !== "name" &&
                          value !== null &&
                          value !== undefined &&
                          value !== "" &&
                          typeof value !== "object"
                      )
                      .slice(0, 8)
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className="mt-2 flex items-start justify-between gap-3 border-t border-gray-100 pt-2 text-sm"
                        >
                          <span className="capitalize text-gray-400">
                            {key.replaceAll("_", " ")}
                          </span>
                          <strong className="max-w-[60%] break-words text-right text-gray-700">
                            {String(value)}
                          </strong>
                        </div>
                      ))}
                </section>
                <section className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                  <label className="text-[10px] font-bold uppercase tracking-wide text-blue-500">
                    Ndrysho statusin
                  </label>
                  <select
                    value={mobileDetailOrder.status}
                    onChange={async (e) => {
                      const nextStatus = e.target.value;
                      await handleStatusChange(
                        mobileDetailOrder.id,
                        nextStatus
                      );
                      setMobileDetailOrder((prev) =>
                        prev
                          ? {
                            ...prev,
                            status: nextStatus,
                          }
                          : prev
                      );
                    }}
                    className="
                mt-2 w-full
                rounded-xl
                border border-blue-200
                bg-white
                px-3 py-3
                text-sm font-bold
                text-gray-800
                outline-none
                focus:border-blue-500
                focus:ring-4
                focus:ring-blue-100
              "
                  >
                    <option value="pending">Në pritje</option>
                    <option value="confirmed">Konfirmuar</option>
                    <option value="shipped">Dërguar</option>
                    <option value="cancelled">Anuluar</option>
                  </select>
                </section>
              </div>
              <div className="border-t border-gray-200 bg-white p-3">
                <button
                  type="button"
                  onClick={() => setMobileDetailOrder(null)}
                  className="
              w-full rounded-xl
              bg-gray-100
              px-4 py-3
              text-sm font-bold
              text-gray-700
              transition
              active:scale-[0.98]
            "
                >
                  Mbyll
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
export default AdminOrders;
const ordersDesign = "/* Vetëm pamja e AdminOrders. Nuk ndryshon fusha ose funksione. */\n.orders-design{background:#f9fafb;color:#111827;font-family:inherit}\n.orders-design main{font-size:14px;line-height:1.5;color:#374151}\n.orders-design main button,.orders-design main input,.orders-design main select,.orders-design main textarea{font-family:inherit;font-size:14px;line-height:1.5;letter-spacing:normal}\n.orders-design main>.mb-6.flex{background:transparent;padding:0;border:0;box-shadow:none}\n.orders-design main h1{font-size:30px;line-height:1.25;font-weight:700;letter-spacing:normal;color:#111827}\n.orders-design main>.mb-6.flex p{font-size:14px;color:#6b7280}\n.orders-design main>.mb-6.flex button{border-radius:12px;background:#2563eb;box-shadow:none;padding:12px 20px;font-weight:600}\n.orders-design main>.mb-6.flex button:hover{background:#1d4ed8}\n.orders-design main>section.border{border-color:#e5e7eb;border-radius:16px;padding:22px 24px;box-shadow:none}\n.orders-design main input,.orders-design main select{border-color:#d1d5db;border-radius:10px;background:#fff;color:#374151;min-height:44px}\n.orders-design main input:focus,.orders-design main select:focus{outline:2px solid #bfdbfe;outline-offset:1px}\n.orders-design main>.border-blue-100{border-color:#e5e7eb;background:#fff;color:#6b7280;border-radius:12px;font-size:14px}\n.orders-design main>section.hidden{border:1px solid #e5e7eb;border-radius:16px;box-shadow:none}\n.orders-design main table{font-size:14px;line-height:1.5}\n.orders-design main thead{background:#f9fafb;color:#6b7280;border-bottom:1px solid #e5e7eb}\n.orders-design main th{padding:16px 20px;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.4px}\n.orders-design main td{padding:18px 20px;font-size:14px;border-color:#f3f4f6}\n.orders-design main tbody tr:hover{background:#f9fafb}\n.orders-design main td.font-bold,.orders-design main td.font-semibold{font-weight:600}\n.orders-design main td span.rounded-full{font-size:12px;font-weight:600;padding:6px 11px}\n.orders-design main td button{width:36px;height:36px;border:1px solid #e5e7eb;border-radius:8px}\n.orders-design main .shadow-sm{box-shadow:none}\n.orders-design main .font-extrabold,.orders-design main .font-black{font-weight:700}\n.orders-design main>.mt-6.flex{gap:10px}\n.orders-design main>.mt-6.flex>button{width:42px;height:42px;padding:0;background:#fff;border:1px solid #e5e7eb;border-radius:9px;color:#374151}\n.orders-design main>.mt-6.flex>span{display:flex;align-items:center;justify-content:center;min-width:90px;height:42px;background:#fff;border:1px solid #e5e7eb;border-radius:9px;font-size:14px;font-weight:500;box-shadow:none}\n.orders-design main dialog{border:1px solid #e5e7eb;border-radius:16px;color:#111827;font-family:inherit;box-shadow:0 30px 100px #182c5040}\n.orders-design main dialog::backdrop{background:#15243a88;backdrop-filter:blur(4px)}\n.orders-design main dialog h2{font-size:22px;font-weight:700;letter-spacing:normal}\n.orders-design main dialog label{font-size:14px;font-weight:500}\n.orders-design main dialog input,.orders-design main dialog textarea,.orders-design main dialog select{font-size:14px;border:1px solid #d1d5db;border-radius:10px}\n.orders-design main .bg-gradient-to-r.from-blue-700{background:#f9fafb;color:#111827;border-color:#e5e7eb}\n.orders-design main .bg-gradient-to-r.from-blue-700 .text-blue-100{color:#6b7280}\n.orders-design main .bg-gradient-to-r.from-blue-700 button{background:#fff;color:#6b7280;border:1px solid #e5e7eb}\n.orders-design main .bg-gradient-to-r.from-blue-700 h2{font-size:20px;font-weight:700}\n.orders-design main .md\\:hidden article{border-color:#e5e7eb;border-radius:16px;box-shadow:none}\n.orders-design main .md\\:hidden h3{font-size:16px;font-weight:600;line-height:1.5}\n.orders-design main button:focus-visible{outline:2px solid #5385ef;outline-offset:3px}\n@media(max-width:767px){.orders-design main h1{font-size:26px}.orders-design main>section.border{padding:18px 16px}.orders-design main>.mb-6.flex p{font-size:13px}}\n\n.orders-design main table{width:100%}.orders-design main .bg-gradient-to-r.from-blue-700{background:#f9fafb;color:#111827;border-bottom:1px solid #e5e7eb}.orders-design main .bg-gradient-to-r.from-blue-700 h2{font-size:22px;font-weight:700}.orders-design main .bg-gradient-to-r.from-blue-700 .text-blue-100{color:#6b7280}.orders-design main .bg-gradient-to-r.from-blue-700 button{background:#fff;color:#6b7280;border:1px solid #e5e7eb}.orders-design main .rounded-3xl{border-radius:16px}.orders-design main .font-black,.orders-design main .font-extrabold{font-weight:700}.orders-design main h3{font-weight:600}.orders-design main .text-xs{font-size:12px}.orders-design main td select{font-size:12px;min-height:36px;font-weight:500}.orders-design main td .rounded-full{font-size:12px;font-weight:600}.orders-design main .shadow-sm{box-shadow:none}\n";